/* eslint-disable no-empty */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import IMask from "imask";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import getFirstAndLastName from "../../../utils/helper";
import "./OneStepOrderForm.scss";

const DEFAULT_REDIRECT = "https://app.unclekam.com/thank-you";

const stripeInputStyle = {
  style: {
    base: {
      fontSize: "14px",
      color: "#212529",
      "::placeholder": { color: "#ADB5BD" },
      fontFamily:
        'Open Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
    },
    invalid: { color: "#dc3545" },
  },
};

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

async function callApi(PROXY_URL, { method = "GET", path, qa_mode, data, params }) {
  const body = { path, method, qa_mode, ...(data ? { data } : {}), ...(params ? { params } : {}) };
  const resp = await axios.post(PROXY_URL, body, { headers: { "Content-Type": "application/json" } });
  return resp;
}

function OneStepOrderFormInner({
  productIds,
  additionalProductIds,
  redirectUrl,
  qa_mode,
  proxyUrl,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

  const phoneRef = useRef(null);
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [products, setProducts] = useState([]);
  const [additionalProducts, setAdditionalProducts] = useState([]);
  const [selectedMain, setSelectedMain] = useState(new Set());
  const [selectedUpsell, setSelectedUpsell] = useState(new Set());
  const [productIdsState, setProductIdsState] = useState([]); // <— NEW

  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });

  // Stripe “filled” (not-empty) flags
  const [cardNumberReady, setCardNumberReady] = useState(false);
  const [cardExpiryReady, setCardExpiryReady] = useState(false);
  const [cardCvcReady, setCardCvcReady] = useState(false);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(
    () => (typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode")),
    [qa_mode, params]
  );

  useEffect(() => {
    if (!phoneRef.current) return;
    const mask = IMask(phoneRef.current, {
      mask: [
        {
          mask: "(000) 000-0000",
          country: "US",
        },
        {
          mask: "+1 (000) 000-0000",
          country: "US_INTL",
        },
        {
          mask: "+995 (000) 000-000",
          country: "GE",
        },
        {
          mask: "+00000000000000",
          country: "INTL",
        },
      ],
      dispatch: (appended, dynamicMasked) => {
        const raw = dynamicMasked.value + appended;
        if (raw.startsWith("+1")) {
          return dynamicMasked.compiledMasks.find((m) => m.country === "US_INTL");
        }
        if (raw.startsWith("+995")) {
          return dynamicMasked.compiledMasks.find((m) => m.country === "GE");
        }
        if (raw.startsWith("+")) {
          return dynamicMasked.compiledMasks.find((m) => m.country === "INTL");
        }
        return dynamicMasked.compiledMasks.find((m) => m.country === "US");
      },
    });
    mask.on("accept", () => setPhone(mask.value));
    return () => mask.destroy();
  }, []);
  // -----------------------------------------------------

  // Fetch products by ids passed via props
  useEffect(() => {
    const mainIds = (Array.isArray(productIds) ? productIds : String(productIds || "").split(","))
      .map((s) => s && String(s).trim())
      .filter(Boolean)
      .map(Number);

    const upsellIds = (Array.isArray(additionalProductIds) ? additionalProductIds : String(additionalProductIds || "").split(","))
      .map((s) => s && String(s).trim())
      .filter(Boolean)
      .map(Number);

    const all = [...mainIds, ...upsellIds];
    if (!all.length) return;

    let cancelled = false;

    (async () => {
      try {
        const idsParam = all.join(",");
        const { data: payload } = await callApi(PROXY_URL, {
          method: "GET",
          path: `/get-products/${idsParam}`,
          qa_mode: qa,
        });

        const list =
          Array.isArray(payload) ? payload :
          Array.isArray(payload?.data) ? payload.data :
          [];

        if (cancelled) return;

        const mainSet = new Set(mainIds);
        const upsellSet = new Set(upsellIds);

        setProducts(list.filter((p) => mainSet.has(Number(p.id))));
        setAdditionalProducts(list.filter((p) => upsellSet.has(Number(p.id))));
      } catch (e) {
        console.log("fetch products failed", e);
        if (!cancelled) {
          setProducts([]);
          setAdditionalProducts([]);
          setAlert({ type: "error", message: "Failed to load products." });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [productIds, additionalProductIds, PROXY_URL, qa]);

  // ---------- helpers ----------
  function getEffectivePrice(p) {
    const d = Number.parseInt(String(p?.discounted_price ?? "").trim(), 10);
    const base = Number.parseInt(String(p?.price ?? "0").trim(), 10) || 0;
    return Number.isFinite(d) && d > 0 ? d : base;
  }
  function hasRealDiscount(p) {
    const base = Number.parseInt(String(p?.price ?? "0").trim(), 10) || 0;
    const eff = getEffectivePrice(p);
    return eff > 0 && base > 0 && eff < base;
  }
  // -----------------------------

  // Keep productIdsState synced with selections (adds/removes automatically)
  useEffect(() => {
    const ids = [
      ...Array.from(selectedMain.values()),
      ...Array.from(selectedUpsell.values()),
    ].map(Number);
    setProductIdsState(ids);
  }, [selectedMain, selectedUpsell]);

  // Total
  const totalPrice = useMemo(() => {
    let sum = 0;
    for (const p of products) {
      if (selectedMain.has(p.id)) sum += getEffectivePrice(p);
    }
    for (const p of additionalProducts) {
      if (selectedUpsell.has(p.id)) sum += getEffectivePrice(p);
    }
    return sum;
  }, [products, additionalProducts, selectedMain, selectedUpsell]);

  // Gate the submit button: fields filled, at least one product, Stripe fields not empty
  const canSubmit = useMemo(() => {
    const nameOk = fullname.trim().length > 0;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const phoneOk = (phone || "").replace(/\D/g, "").length >= 10;
    const hasSelection = selectedMain.size > 0 || selectedUpsell.size > 0;
    const stripeReady = !!(stripe && elements);
    const cardFilled = cardNumberReady && cardExpiryReady && cardCvcReady;
    return nameOk && emailOk && phoneOk && hasSelection && stripeReady && cardFilled;
  }, [
    fullname,
    email,
    phone,
    selectedMain,
    selectedUpsell,
    stripe,
    elements,
    cardNumberReady,
    cardExpiryReady,
    cardCvcReady,
  ]);

  const toggleMain = (id) =>
    setSelectedMain((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleUpsell = (id) =>
    setSelectedUpsell((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const clearForm = () => {
    setFullname("");
    setEmail("");
    setPhone("");
    setSelectedMain(new Set());
    setSelectedUpsell(new Set());
    setProductIdsState([]); // reset ids too
    const n = elements?.getElement(CardNumberElement);
    const e = elements?.getElement(CardExpiryElement);
    const c = elements?.getElement(CardCvcElement);
    n && n.clear();
    e && e.clear();
    c && c.clear();
  };

  const setError = (msg) =>
    setAlert({ type: "error", message: msg || "Something went wrong" });

  const onSubmit = async (e) => {
    e.preventDefault();
    setAlert({ type: "", message: "" });

    if ((!products.length && !additionalProducts.length) || (!selectedMain.size && !selectedUpsell.size)) {
      setError("No Product Selected");
      return;
    }
    if (!stripe || !elements) {
      setError("Stripe is not ready yet.");
      return;
    }

    try {
      setSubmitting(true);

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
      });

      if (error) {
        setSubmitting(false);
        setError(error.message);
        return;
      }

      // Pick the first selected product from main or upsell (fallback to first available)
      const firstSelectedId =
        [...selectedMain][0] ??
        [...selectedUpsell][0] ??
        products[0]?.id ??
        additionalProducts[0]?.id;

      const firstProduct =
        [...products, ...additionalProducts].find((p) => p.id === firstSelectedId) ||
        products[0] ||
        additionalProducts[0];

      const subscription = (() => {
        try { return JSON.parse(firstProduct?.subscription || "{}"); }
        catch { return {}; }
      })();

      const { firstName, lastName } = getFirstAndLastName(fullname || "");
      const roleId = Number(firstProduct?.role_id) || undefined;

      const orderData = {
        firstname: firstName,
        lastname: lastName,
        email,
        phone,
        role_id: roleId,
        invoice_name: "Unclekam Product",
        amount: totalPrice,
        cardData: paymentMethod,
        productIds: productIdsState, // <— SEND SELECTED PRODUCT IDS
      };

      if (String(subscription?.is_subscription) === "true") {
        orderData.subscription = {
          is_subscription: subscription?.is_subscription,
          trial_dates: subscription?.trial_dates,
          type_of_subscription: subscription?.type_of_subscription,
        };
      }

      const response = await callApi(PROXY_URL, {
        method: "POST",
        path: "/pay-product",
        qa_mode: qa,
        data: orderData,
      });

      if (response?.data?.success || response?.data?.id) {
        setAlert({ type: "success", message: "Payment Successful!" });
        clearForm();
        const baseUrl = (redirectUrl || DEFAULT_REDIRECT).trim();
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set("email", email);
        if (url) window.location.href = url.toString();
      } else {
        setError(`Error: ${response?.data?.message || "Payment failed"}`);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container px-0" style={{ maxWidth: 600 }}>
      <form className="w-100 bg-transparent rounded-3 p-3" onSubmit={onSubmit}>
        {/* Full Name */}
        <div className="mb-3">
          <input
            type="text"
            required
            placeholder="Full Name"
            name="fullname"
            className="form-control"
            value={fullname}
            onChange={(e) => setFullname(e.target.value)}
          />
        </div>

        {/* Email */}
        <div className="mb-3">
          <input
            type="email"
            required
            placeholder="Email Address"
            name="email"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {/* Phone */}
        <div className="mb-3">
          <input
            type="text"
            required
            placeholder="Phone Number"
            name="phone"
            className="form-control"
            ref={phoneRef}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        {/* Payment header */}
        <div className="d-flex align-items-center gap-2 mt-2">
          <h3 className="text-uppercase fs-6 fw-bold text-grey-400 mb-0">Payment</h3>
          <div className="osof-hr-line" />
        </div>

        {/* Order table */}
        <div className="mt-3 ">
          <table className="table mb-0 osof-table">
            <thead>
              <tr>
                <th className="fw-semibold">Item</th>
                <th className="text-end fw-semibold">Quantity</th>
                <th className="text-end fw-semibold">Price</th>
              </tr>
            </thead>

            <tbody id="order-table-list">
              {products.map((p) => {
                const sub = (() => {
                  try { return JSON.parse(p?.subscription || "{}"); }
                  catch { return {}; }
                })();

                const now = getEffectivePrice(p);

                return (
                  <tr key={p.id}>
                    <td>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`product-${p.id}`}
                          checked={selectedMain.has(p.id)}
                          onChange={() => toggleMain(p.id)}
                          data-id={p.id}
                          data-price={now}
                          data-role-id={p.role_id}
                          data-is-subscription={sub?.is_subscription ? "true" : "false"}
                          data-trial-date={sub?.trial_dates || ""}
                          data-subscription-type={sub?.type_of_subscription || ""}
                        />
                        <label
                          className="form-check-label ms-2"
                          htmlFor={`product-${p.id}`}
                          style={{ cursor: "pointer" }}
                        >
                          {p.name}
                        </label>
                      </div>
                    </td>
                    <td className="text-end fw-bold">1</td>
                    <td className="text-end fw-bold">
                      {hasRealDiscount(p) && (
                        <span className="osof-old-price me-1">
                          ${Number.parseInt(p.price, 10)}
                        </span>
                      )}
                      <span className="text-primary">${now}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={2} className="fw-semibold">Order Total</td>
                <td className="text-end fw-bold text-primary">${totalPrice}</td>
              </tr>
            </tfoot>
          </table>

          {/* Upsell */}
          {additionalProducts.length > 0 && (
            <div className="osof-offer-box mt-3" id="special-offer-checkbox">
              {additionalProducts.map((p) => {
                const now = getEffectivePrice(p);
                return (
                  <div key={p.id} className="mb-2">
                    <h3 className="h6">
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        id={`additional-${p.id}`}
                        checked={selectedUpsell.has(p.id)}
                        onChange={() => toggleUpsell(p.id)}
                        data-id={p.id}
                        data-price={now}
                      />
                      <label htmlFor={`additional-${p.id}`} className="ms-1" style={{ cursor: "pointer" }}>
                        Comprehensive Tax Analysis
                      </label>
                    </h3>
                    <p className="mb-0">
                      <strong style={{ color: "red" }}>
                        ONLY ${now} ONE TIME OFFER&nbsp; Maximize Your Tax Savings!
                      </strong>
                      <br />
                      Let our tax strategists review your past returns and uncover
                      customized strategies to help you save more. Get your tax
                      analysis report with custom tax strategies based on your
                      unique situation. <br />
                      {hasRealDiscount(p) ? (
                        <>
                          This is usually <span className="osof-old-price">${Number.parseInt(p.price, 10)}</span>, but get it for just ${now} today.
                        </>
                      ) : (
                        <>This is ${now}.</>
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Card fields */}
        <div className="row g-3 mt-3">
          <div className="col-12 col-md-6">
            <label className="form-label fw-semibold">Card Number</label>
            <div className="osof-stripe">
              <CardNumberElement
                options={stripeInputStyle}
                onChange={(e) => setCardNumberReady(!e.empty)}
              />
            </div>
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label fw-semibold">Expiration</label>
            <div className="osof-stripe">
              <CardExpiryElement
                options={stripeInputStyle}
                onChange={(e) => setCardExpiryReady(!e.empty)}
              />
            </div>
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label fw-semibold">CVC</label>
            <div className="osof-stripe">
              <CardCvcElement
                options={stripeInputStyle}
                onChange={(e) => setCardCvcReady(!e.empty)}
              />
            </div>
          </div>
        </div>

        {/* Alerts */}
        {alert.type === "error" && (
          <div className="alert alert-danger mt-3 mb-2" role="alert">
            {alert.message}
          </div>
        )}
        {alert.type === "success" && (
          <div className="alert alert-success mt-3 mb-2" role="alert">
            {alert.message}
          </div>
        )}

        {/* Submit */}
        <div className="mt-3">
          <button
            type="submit"
            className="btn btn-primary osof-btn text-white w-100 d-flex align-items-center justify-content-center fw-bold fs-5 py-3"
            disabled={submitting || !canSubmit}
          >
            {submitting ? (
              <span className="osof-loader" />
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="26"
                  height="26"
                  fill="white"
                  className="me-2"
                >
                  <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm0 2c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm10-2c-1.1 0-1.99.9-1.99 2S15.9 22 17 22s2-.9 2-2-.9-2-2-2zm0 2c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zM7.04 11h9.91c.82 0 1.54-.5 1.85-1.25L21.79 4H6.21l-.95-2H1v2h3l3.6 7.59-1.35 2.45C5.08 14.9 5 15.15 5 15.5c0 .83.67 1.5 1.5 1.5h12v-2H7.42c-.12 0-.25-.08-.32-.19L8.1 13h8.88c1.38 0 2.63-.86 3.12-2.15L22 4H6.21l.83 1.59h14.6c.17 0 .31-.1.38-.24.07-.13.05-.28-.05-.4L19.1 4H7.7l-.66 1.25C7.04 5.32 7 5.65 7 6v5zm10-1H8V6h9v4z" />
                </svg>
                Complete Order
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function OneStepOrderForm({
  productIds = [],
  additionalProductIds = [],
  testing = false,
  redirectUrl = DEFAULT_REDIRECT,
  testKey = import.meta.env.VITE_STRIPE_STAGING_KEY,
  liveKey = import.meta.env.VITE_STRIPE_PROD_KEY,
  qa_mode,
  proxyUrl,
}) {
  const qaModeURL = new URLSearchParams(window.location.search).has("qa_mode");
  const useTest = Boolean(testing || qa_mode || qaModeURL);
  const activeKey = useTest ? testKey : liveKey;
  const stripePromise = useMemo(
    () => loadStripe(activeKey),
    [activeKey]
  );

  return (
    <Elements key={activeKey} stripe={stripePromise}>
      <OneStepOrderFormInner
        productIds={productIds}
        additionalProductIds={additionalProductIds}
        redirectUrl={redirectUrl}
        qa_mode={useTest}
        proxyUrl={proxyUrl}
      />
    </Elements>
  );
}
